import {useCallback, useEffect, useRef} from 'react';
import {TrackEventUBA, getUbaPageName} from '@jobseeker/common-core/utils';
import {locations} from '@jobseeker/lotus/components/JobSearchBar/data';

export interface JobVisibilityData {
  jobId: string;
  refCode: string;
  jobPos: number;
}

export interface UseJobVisibilityTrackerOptions {
  batchSize?: number;
  categoryID?: string;
  categoryName?: string;
  tagID?: string;
  tagName?: string;
  /** Enable/disable tracking. Defaults to true only for IIMJobs. */
  enabled?: boolean;
}

export const useJobVisibilityTracker = (
  options: UseJobVisibilityTrackerOptions = {}
) => {
  const {
    batchSize = 5,
    categoryID,
    categoryName,
    tagID,
    tagName,
    enabled,
  } = options;

  const isEnabled = enabled ?? process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  // Set to track already-viewed job IDs (prevents duplicates)
  const viewedJobIdsRef = useRef<Set<string>>(new Set());
  // Array to collect current batch of job data (jobId + refCode)
  const currentBatchRef = useRef<JobVisibilityData[]>([]);
  // Map of element -> job data for observer callback lookup
  const elementToJobDataRef = useRef<Map<Element, JobVisibilityData>>(
    new Map()
  );
  // Single IntersectionObserver instance
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Debounce timer for flushing remaining jobs
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to convert location labels to IDs
  const getLocationIds = (locParam: string): string => {
    if (!locParam) return '';
    const locationLabels = locParam.split('_');
    const ids: number[] = [];

    locationLabels.forEach(label => {
      const location = locations.find(
        (loc: {label: string; value: number; selected: number[]}) =>
          loc.label.replace(/[/\s]/g, '-') === label || loc.label === label
      );
      if (location) {
        if (location.selected && location.selected.length > 0) {
          ids.push(...location.selected);
        } else {
          ids.push(location.value);
        }
      }
    });

    // Remove duplicates and return comma-separated
    return Array.from(new Set(ids)).join(',');
  };

  const fireTrackingEvent = useCallback(
    (jobs: JobVisibilityData[]) => {
      if (jobs.length === 0) return;
      const pageName = getUbaPageName();
      const pageNo =
        typeof window !== 'undefined'
          ? parseInt(
              new URLSearchParams(window.location.search).get('page') || '1',
              10
            )
          : 1;
      // console.log('##### FIRING TRACKING EVENT #####');
      // console.log('##### Jobs in batch:', jobs);
      // console.log('##### PageName:', pageName, 'PageNo:', pageNo);
      // console.log('##### Total viewed so far:', viewedJobIdsRef.current.size);

      const urlParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null;

      // Get jobID based on page type
      let jobID = '';
      if (pageName === 'Similar Jobs Page' && urlParams) {
        jobID = urlParams.get('jobId') || '';
      } else if (
        pageName === 'Job Description Page' &&
        typeof window !== 'undefined'
      ) {
        const pathMatch = window.location.pathname.match(/-(\d+)$/);
        if (pathMatch && pathMatch[1]) {
          jobID = pathMatch[1];
        }
      }

      let keywords = '';
      if (
        pageName.toLowerCase().includes('search') &&
        typeof window !== 'undefined'
      ) {
        // For AI Search, get keyword from query param
        if (pageName.toLowerCase().includes('ai search')) {
          keywords = urlParams?.get('query') || '';
        } else {
          // For regular search, get from pathname
          const pathMatch = window.location.pathname.match(
            /\/search\/([^/]+)-jobs/
          );
          if (pathMatch && pathMatch[1]) {
            keywords = pathMatch[1];
          }
        }
      }

      // Extract filter params from URL
      const locParam = urlParams?.get('loc') || '';
      const filterLocation = getLocationIds(locParam);
      const filterIndustry = urlParams?.get('industry') || '';
      let minExp = urlParams?.get('minexp') || '';
      let maxExp = urlParams?.get('maxexp') || '';

      // Fallback to localStorage if minexp/maxexp not in URL
      if (!minExp && !maxExp && typeof window !== 'undefined') {
        try {
          const isCatOrTagPage =
            pageName.toLowerCase().includes('category') ||
            pageName.toLowerCase().includes('tag');
          const storageKey = isCatOrTagPage ? 'filterCat' : 'filter';
          const storedFilter = localStorage.getItem(storageKey);
          if (storedFilter) {
            const filterData = JSON.parse(storedFilter);
            minExp = filterData.minexp || '';
            maxExp = filterData.maxexp || '';
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      const filterExp = minExp || maxExp ? `${minExp}-${maxExp}` : '';

      // Use setTimeout to defer tracking and prevent main thread blocking
      setTimeout(() => {
        TrackEventUBA('jobTuppleView', 'view', {
          viewedJobs: jobs.map(job => JSON.stringify(job)),
          page: pageName,
          pageName: pageName,
          pageNo: pageNo,
          ...(jobID && {jobID}),
          ...(keywords && {keywords}),
          ...(categoryID && {categoryID}),
          ...(categoryName && {categoryName}),
          ...(tagID && {tagID}),
          ...(tagName && {tagName}),
          ...(filterExp && {filterExp}),
          ...(filterIndustry && {filterIndustry}),
          ...(filterLocation && {filterLocation}),
        });
      }, 0);
    },
    [categoryID, categoryName, tagID, tagName]
  );

  const flush = useCallback(() => {
    if (currentBatchRef.current.length > 0) {
      // console.log('##### FLUSHING REMAINING JOBS #####');
      // console.log('##### Current batch before flush:', currentBatchRef.current);
      fireTrackingEvent([...currentBatchRef.current]);
      currentBatchRef.current = [];
    }
  }, [fireTrackingEvent]);

  /**
   * Reset tracking state - flushes current batch and clears viewed set
   * Call this when filters change to start fresh tracking
   */
  const reset = useCallback(() => {
    // console.log('##### RESETTING TRACKER - Filter change #####');
    // First flush any remaining jobs
    flush();
    // Clear the viewed set
    viewedJobIdsRef.current.clear();
    // Clear element map
    elementToJobDataRef.current.clear();
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    // console.log('##### Tracker reset complete #####');
  }, [flush]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const jobData = elementToJobDataRef.current.get(entry.target);
        if (!jobData) return;
        // Skip if already viewed
        if (viewedJobIdsRef.current.has(jobData.jobId)) return;
        // Mark as viewed
        viewedJobIdsRef.current.add(jobData.jobId);
        // Add to current batch
        currentBatchRef.current.push(jobData);
        // console.log('##### JOB VIEWED #####');
        // console.log('##### Job Data:', jobData);
        // console.log('##### Current batch:', [...currentBatchRef.current]);
        // console.log('##### Viewed set:', Array.from(viewedJobIdsRef.current));
        console.log(
          '##### Batch size:',
          currentBatchRef.current.length,
          '/',
          batchSize
        );

        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        if (currentBatchRef.current.length >= batchSize) {
          fireTrackingEvent([...currentBatchRef.current]);
          currentBatchRef.current = [];
        } else {
          // Set debounce timer to flush remaining jobs after 5 seconds of inactivity
          debounceTimerRef.current = setTimeout(() => {
            if (currentBatchRef.current.length > 0) {
              // console.log('##### DEBOUNCED FLUSH - 5s inactivity #####');
              flush();
            }
          }, 5000);
        }
      });
    },
    [batchSize, fireTrackingEvent, flush]
  );

  useEffect(() => {
    // Skip if disabled
    if (!isEnabled) return;

    // Only create observer on client side
    if (
      typeof window === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.2,
    });

    return () => {
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Flush remaining on unmount
      flush();
      // Cleanup observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // Clear maps
      elementToJobDataRef.current.clear();
    };
  }, [handleIntersection, flush, isEnabled]);

  /**
   * Register a job element for visibility tracking
   * Use as ref callback: ref={(el) => registerJobElement(el, jobId, refCode, jobPos)}
   */
  const registerJobElement = useCallback(
    (
      element: Element | null,
      jobId: string,
      refCode: string = '',
      jobPos: number = 0
    ) => {
      if (!isEnabled || !observerRef.current) return;

      // Skip if already viewed
      if (viewedJobIdsRef.current.has(jobId)) return;

      if (element) {
        // Register new element with job data
        elementToJobDataRef.current.set(element, {jobId, refCode, jobPos});
        observerRef.current.observe(element);
      }
    },
    [isEnabled]
  );

  /**
   * Unregister a job element (call when component unmounts or element changes)
   */
  const unregisterJobElement = useCallback((element: Element | null) => {
    if (!element || !observerRef.current) return;

    observerRef.current.unobserve(element);
    elementToJobDataRef.current.delete(element);
  }, []);

  return {
    /** Register an element for visibility tracking */
    registerJobElement,
    /** Unregister an element from tracking */
    unregisterJobElement,
    /** Force fire tracking for remaining jobs in batch */
    flush,
    /** Reset tracking - flushes batch and clears viewed set (call on filter changes) */
    reset,
    /** Get count of jobs currently in batch (for debugging) */
    getCurrentBatchSize: () => currentBatchRef.current.length,
    /** Get count of total viewed jobs (for debugging) */
    getTotalViewedCount: () => viewedJobIdsRef.current.size,
  };
};

export default useJobVisibilityTracker;
